"use client"

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react"
import { createPortal } from "react-dom"

// ---------------------------------------------------------------------------
// Global tooltip coordination (single-tooltip-at-a-time)
//
// Problem (#40): Multiple InstantTooltip instances each manage their own
// visibility via independent useState hooks.  When the user moves quickly
// between two adjacent interactive tooltips (e.g. Info icon -> Completion
// Criteria icon), the previous tooltip's portal can persist at z-[9999]
// with pointer-events:auto, covering the viewport and blocking all
// interaction — requiring a full page reload to recover.
//
// Solution: A lightweight module-level event bus.  Every tooltip subscribes
// to a "dismiss" signal.  When any tooltip opens it broadcasts "dismiss"
// first, causing all *other* visible tooltips to close synchronously before
// the new one renders its portal.
//
// Additional safeguards:
//   - Scroll dismiss: any scroll event on the page dismisses all tooltips
//   - Escape key: pressing Escape dismisses the active tooltip
//   - Click-outside: clicking outside interactive tooltips dismisses them
//   - Safety timeout: interactive tooltips auto-dismiss after 15s max
// ---------------------------------------------------------------------------
type DismissListener = (senderId: number) => void
let _nextTooltipId = 0
const _dismissListeners = new Set<DismissListener>()

/** Max lifetime for interactive tooltips (ms). Prevents "stuck" tooltips.
 *  Set generously (15s) so it doesn't interfere with reading long content,
 *  but catches tooltips that escaped all other dismiss mechanisms. */
const INTERACTIVE_TOOLTIP_MAX_LIFETIME_MS = 15000

// ---------------------------------------------------------------------------
// Layer 2: Orphan detector — tracks active tooltips and polls for dead triggers
// ---------------------------------------------------------------------------
interface ActiveTooltipEntry {
  dismiss: () => void
  triggerRef: React.RefObject<HTMLSpanElement | null>
}
const _activeTooltips = new Map<number, ActiveTooltipEntry>()
let _orphanPollInterval: ReturnType<typeof setInterval> | null = null
const ORPHAN_POLL_MS = 200

function _startOrphanPolling() {
  if (_orphanPollInterval !== null) return
  _orphanPollInterval = setInterval(() => {
    if (_activeTooltips.size === 0) {
      _stopOrphanPolling()
      return
    }
    _activeTooltips.forEach((entry, id) => {
      const trigger = entry.triggerRef.current
      if (!trigger || !document.body.contains(trigger)) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Orphan detected: trigger removed from DOM (id=%d)', id)
        }
        entry.dismiss()
        return
      }
      const rect = trigger.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Orphan detected: trigger collapsed to zero dimensions (id=%d)', id)
        }
        entry.dismiss()
      }
    })
  }, ORPHAN_POLL_MS)
}

function _stopOrphanPolling() {
  if (_orphanPollInterval !== null) {
    clearInterval(_orphanPollInterval)
    _orphanPollInterval = null
  }
}

function _registerActiveTooltip(id: number, entry: ActiveTooltipEntry) {
  _activeTooltips.set(id, entry)
  _startOrphanPolling()
}

function _unregisterActiveTooltip(id: number) {
  _activeTooltips.delete(id)
  if (_activeTooltips.size === 0) {
    _stopOrphanPolling()
  }
}

// ---------------------------------------------------------------------------
// Layer 3: Global click safety valve — last-resort orphan cleanup on any click
// ---------------------------------------------------------------------------
let _safetyValveAttached = false
function _ensureSafetyValve() {
  if (_safetyValveAttached || typeof window === 'undefined') return
  _safetyValveAttached = true
  document.addEventListener('pointerdown', () => {
    // On any click, check for orphaned tooltips whose triggers are gone
    _activeTooltips.forEach((entry, id) => {
      const trigger = entry.triggerRef.current
      if (!trigger || !document.body.contains(trigger)) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Safety valve dismissed orphan on click (id=%d)', id)
        }
        entry.dismiss()
        return
      }
      const rect = trigger.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Safety valve dismissed collapsed trigger on click (id=%d)', id)
        }
        entry.dismiss()
      }
    })
  }, true) // capture phase
}

// ---------------------------------------------------------------------------
// Broadcast dismiss coordination
// ---------------------------------------------------------------------------
function subscribeDismiss(listener: DismissListener) {
  _dismissListeners.add(listener)
  return () => { _dismissListeners.delete(listener) }
}

function broadcastDismiss(senderId: number) {
  _dismissListeners.forEach(fn => fn(senderId))
}

/** Dismiss ALL visible tooltips (used by scroll handler). */
function broadcastDismissAll() {
  _dismissListeners.forEach(fn => fn(-1))
}

// Global scroll listener — dismiss all tooltips on ANY scroll event.
// Registered once when the module loads (client-side only).
// Tooltips use position:fixed but their triggers scroll with the container,
// causing the tooltip to "float" detached from the trigger after scroll.
let _scrollListenerAttached = false
function ensureScrollListener() {
  if (_scrollListenerAttached || typeof window === 'undefined') return
  _scrollListenerAttached = true
  document.addEventListener('scroll', () => {
    broadcastDismissAll()
  }, { capture: true, passive: true })
}

// ---------------------------------------------------------------------------
// Accessible name for icon-only children (WCAG 4.1.2)
// ---------------------------------------------------------------------------
const UNNAMED_CONTROL_SELECTOR =
  'button, a[href], [role="button"], [role="link"], [role="tab"], [role="menuitem"]'
const TOOLTIP_NAME_MARK = 'data-tooltip-name'

// Which tooltip instance named a control, so a tooltip wrapping another tooltip
// never renames (or strips) the name the inner one gave.
const tooltipNameOwner = new WeakMap<Element, object>()
// What can change a child's name on its own, without the tooltip re-rendering.
const NAME_OBSERVE_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['aria-label', 'aria-labelledby', 'title', 'alt'],
}

/** A name from the control itself; `ownAriaLabel` false ignores its aria-label (the one we may have set). */
function hasAccessibleName(el: Element, ownAriaLabel = true): boolean {
  if (ownAriaLabel && el.getAttribute('aria-label')?.trim()) return true
  if (el.getAttribute('aria-labelledby')?.trim()) return true
  if (el.getAttribute('title')?.trim()) return true
  if (el.textContent?.trim()) return true
  return !!el.querySelector('img[alt]:not([alt=""]), svg title, [aria-label]:not([aria-label=""])')
}

function releaseTooltipName(el: Element) {
  // Remove only what we still own: a caller's own label must survive.
  if (el.getAttribute('aria-label') === el.getAttribute(TOOLTIP_NAME_MARK)) el.removeAttribute('aria-label')
  el.removeAttribute(TOOLTIP_NAME_MARK)
  tooltipNameOwner.delete(el)
}

/**
 * Give `text` as aria-label to every unnamed control under `root`. TOOLTIP_NAME_MARK
 * holds the exact text we set: if the caller later gives the control its own
 * aria-label (value no longer matches) or the control gains its own name (text,
 * title, labelled image), we step aside. Writes only on change, so it is safe to
 * call from the observer that its own writes wake.
 */
function syncTooltipNames(root: Element, text: string, owner: object) {
  root.querySelectorAll(UNNAMED_CONTROL_SELECTOR).forEach(el => {
    const ours = el.getAttribute(TOOLTIP_NAME_MARK)
    if (ours !== null) {
      const by = tooltipNameOwner.get(el)
      if (by && by !== owner) return
      if (el.getAttribute('aria-label') !== ours || hasAccessibleName(el, false)) {
        releaseTooltipName(el)
      } else if (ours !== text) {
        el.setAttribute('aria-label', text)
        el.setAttribute(TOOLTIP_NAME_MARK, text)
      }
      return
    }
    if (hasAccessibleName(el)) return
    el.setAttribute('aria-label', text)
    el.setAttribute(TOOLTIP_NAME_MARK, text)
    tooltipNameOwner.set(el, owner)
  })
}

interface InstantTooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  /** Position relative to trigger - top, bottom, left, or right */
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  /** Tooltip glow intensity */
  glow?: 'default' | 'white'
  /** Milliseconds to wait before hiding tooltip after mouse leaves (default: 0 for instant hide) */
  hideDelay?: number
  /** Allow hovering over the tooltip itself to keep it visible (default: false). When true, default hideDelay becomes 100ms to allow mouse movement to tooltip. */
  interactive?: boolean
  /**
   * CSS selector, evaluated against the trigger's own subtree at hover time.
   * When set, the tooltip only appears if the matched element is currently
   * NOT rendered (display:none / zero client rects) — i.e. the on-screen
   * content it duplicates is hidden. Used by the tab buttons to show the tab
   * name only when its label has collapsed to icon-only (the `.tab-density`
   * container query, or the explicit `iconOnly` prop where no label exists).
   * If nothing matches the selector, the tooltip shows (content is absent).
   */
  showOnlyIfHidden?: string
}

export function InstantTooltip({
  content,
  children,
  position = 'top',
  className = '',
  glow = 'white',
  hideDelay,
  interactive = false,
  showOnlyIfHidden
}: InstantTooltipProps) {
  // Stable unique id for this tooltip instance (survives re-renders)
  const idRef = useRef<number>(-1)
  if (idRef.current === -1) {
    idRef.current = _nextTooltipId++
  }

  // Layer 1: Synchronous visibility ref — prevents stale portals during React's
  // async setState gap. Checked in the render guard alongside isVisible.
  const visibleRef = useRef(false)

  // Attach global scroll listener + safety valve on first render
  useEffect(() => {
    ensureScrollListener()
    _ensureSafetyValve()
  }, [])

  // Instant hide by default - delayed hiding causes issues with many tooltips
  // Interactive tooltips get a small delay to allow mouse movement to tooltip content
  const effectiveHideDelay = hideDelay ?? (interactive ? 100 : 0)
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null)
  const [isPositioned, setIsPositioned] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clear any pending hide timeout
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  // Clear safety auto-dismiss timeout
  const clearSafetyTimeout = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current)
      safetyTimeoutRef.current = null
    }
  }, [])

  // Immediate dismiss helper (no delay, no animation)
  const dismissImmediately = useCallback(() => {
    visibleRef.current = false // Layer 1: synchronous guard
    _unregisterActiveTooltip(idRef.current) // Layer 2: stop tracking
    clearHideTimeout()
    clearSafetyTimeout()
    setIsVisible(false)
    setIsPositioned(false)
    setTooltipPosition(null)
  }, [clearHideTimeout, clearSafetyTimeout])

  // Schedule hiding with delay
  const scheduleHide = useCallback(() => {
    clearHideTimeout()
    if (effectiveHideDelay > 0) {
      hideTimeoutRef.current = setTimeout(() => {
        visibleRef.current = false // Layer 1: synchronous guard
        _unregisterActiveTooltip(idRef.current) // Layer 2: stop tracking
        setIsVisible(false)
        setIsPositioned(false)
        setTooltipPosition(null)
      }, effectiveHideDelay)
    } else {
      visibleRef.current = false // Layer 1: synchronous guard
      _unregisterActiveTooltip(idRef.current) // Layer 2: stop tracking
      setIsVisible(false)
      setIsPositioned(false)
      setTooltipPosition(null)
    }
  }, [effectiveHideDelay, clearHideTimeout])

  // Show tooltip immediately, dismissing all other tooltips first
  const showTooltip = useCallback(() => {
    // Conditional suppression: when `showOnlyIfHidden` is set, only show the
    // tooltip if the matched element inside the trigger is currently hidden.
    // A tab label collapsed to icon-only (via the .tab-density container query)
    // renders with display:none → no client rects → tooltip shows. When the
    // label is on screen, the tooltip would just repeat it, so skip it. Checked
    // at hover time so it always reflects the live responsive state. (No match
    // means the content is absent — e.g. explicit iconOnly mode — so show it.)
    if (showOnlyIfHidden && triggerRef.current) {
      const gate = triggerRef.current.querySelector(showOnlyIfHidden) as HTMLElement | null
      if (gate && gate.getClientRects().length > 0) {
        return
      }
    }
    // Dismiss every other visible tooltip before opening this one
    broadcastDismiss(idRef.current)
    clearHideTimeout()
    clearSafetyTimeout()
    visibleRef.current = true // Layer 1: synchronous guard
    setIsVisible(true)

    // Layer 2: Register in active tooltips map for orphan detection
    _registerActiveTooltip(idRef.current, {
      dismiss: () => {
        visibleRef.current = false
        _unregisterActiveTooltip(idRef.current)
        clearHideTimeout()
        clearSafetyTimeout()
        setIsVisible(false)
        setIsPositioned(false)
        setTooltipPosition(null)
      },
      triggerRef,
    })

    // Safety net: auto-dismiss interactive tooltips after max lifetime.
    // This prevents "stuck" tooltips if all other dismiss mechanisms fail.
    if (interactive) {
      safetyTimeoutRef.current = setTimeout(() => {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Safety auto-dismiss after %dms (id=%d)', INTERACTIVE_TOOLTIP_MAX_LIFETIME_MS, idRef.current)
        }
        visibleRef.current = false
        _unregisterActiveTooltip(idRef.current)
        setIsVisible(false)
        setIsPositioned(false)
        setTooltipPosition(null)
      }, INTERACTIVE_TOOLTIP_MAX_LIFETIME_MS)
    }
  }, [clearHideTimeout, clearSafetyTimeout, interactive, showOnlyIfHidden])

  // Listen for dismiss broadcasts from other tooltip instances (or scroll events)
  useEffect(() => {
    return subscribeDismiss((senderId) => {
      // senderId === -1 means "dismiss all" (from scroll handler)
      if (senderId !== idRef.current) {
        dismissImmediately()
      }
    })
  }, [dismissImmediately])

  // Clean up all timeouts and active tooltip registration on unmount
  useEffect(() => {
    const tooltipId = idRef.current
    return () => {
      clearHideTimeout()
      clearSafetyTimeout()
      _unregisterActiveTooltip(tooltipId)
      visibleRef.current = false
    }
  }, [clearHideTimeout, clearSafetyTimeout])

  // Preserve tooltip across parent re-renders: if the trigger element is still
  // hovered after a React reconciliation, restore visibility. Parent re-renders
  // (e.g., Redux state updates on SuggestedActionCard) can cause the tooltip
  // state to reset even though the mouse never left the trigger.
  useEffect(() => {
    if (isVisible || !triggerRef.current) return
    // Check if the mouse is currently over the trigger element
    const el = triggerRef.current
    const hovered = el.matches(':hover')
    if (hovered && !visibleRef.current) {
      // Mouse is still over trigger but tooltip was dismissed by re-render — restore it
      showTooltip()
    }
  })

  // Reconcile the `showOnlyIfHidden` gate on layout changes. The gate is only
  // evaluated on mouse-enter/focus, but the gated element's visibility is driven
  // by a CSS container query (`.tab-density`) that can flip while the pointer or
  // focus is already on the trigger (e.g. the panel resizes / split-view toggles).
  // A ResizeObserver on the trigger (its size changes when a label collapses or
  // expands) keeps the tooltip correct in BOTH directions while engaged:
  //   - label becomes visible  → dismiss the now-redundant tooltip
  //   - label becomes hidden   → show it, if the trigger is still hovered/focused
  useEffect(() => {
    if (!showOnlyIfHidden || typeof ResizeObserver === 'undefined') return
    const trigger = triggerRef.current
    if (!trigger) return
    const reconcile = () => {
      const el = triggerRef.current
      if (!el) return
      const gate = el.querySelector(showOnlyIfHidden) as HTMLElement | null
      const labelVisible = !!(gate && gate.getClientRects().length > 0)
      const engaged = el.matches(':hover') || el.contains(document.activeElement)
      if (labelVisible) {
        if (visibleRef.current) dismissImmediately()
      } else if (engaged && !visibleRef.current) {
        showTooltip()
      }
    }
    const ro = new ResizeObserver(reconcile)
    ro.observe(trigger)
    return () => ro.disconnect()
  }, [showOnlyIfHidden, dismissImmediately, showTooltip])

  // Dismiss on Escape key press (works for all tooltips, not just interactive)
  useEffect(() => {
    if (!isVisible) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismissImmediately()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isVisible, dismissImmediately])

  // Dismiss interactive tooltips on any click outside the tooltip and its trigger.
  // This prevents the tooltip (z-[9999], pointer-events: auto) from blocking clicks
  // on distant UI elements like the End Call button when the tooltip content
  // physically overlaps them via portal positioning.
  useEffect(() => {
    if (!interactive || !isVisible) return

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      // Keep tooltip open if clicking inside the tooltip or trigger
      if (tooltipRef.current?.contains(target)) return
      if (triggerRef.current?.contains(target)) return
      // Dismiss immediately
      dismissImmediately()
    }

    // Use capture phase so the tooltip is dismissed BEFORE the click reaches
    // the target element, preventing the tooltip from swallowing the event.
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [interactive, isVisible, dismissImmediately])

  // WCAG 4.1.2 (Q-2214): an icon-only control wrapped by a tooltip has no accessible
  // name. Give the tooltip text to any interactive child that has none (no text,
  // aria-label, aria-labelledby, title or labelled image). A control that is
  // already named is never touched, and only attributes added here are removed.
  // The child can also change on its own (its own state), so a MutationObserver
  // re-checks between our renders.
  const nameOwnerToken = useRef({})
  useEffect(() => {
    const root = triggerRef.current
    if (!root || typeof content !== 'string' || !content) return
    const owner = nameOwnerToken.current
    syncTooltipNames(root, content, owner)
    const MO = root.ownerDocument.defaultView?.MutationObserver
    const observer = MO ? new MO(() => syncTooltipNames(root, content, owner)) : null
    observer?.observe(root, NAME_OBSERVE_OPTIONS)
    return () => {
      observer?.disconnect()
      root.querySelectorAll(`[${TOOLTIP_NAME_MARK}]`).forEach(el => {
        if ((tooltipNameOwner.get(el) ?? owner) === owner) releaseTooltipName(el)
      })
    }
  }, [content, children])

  // Ensure we only render portal on client
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Use useLayoutEffect for synchronous position calculation before browser paint
  // This prevents the flash at (0,0) before moving to correct position
  useLayoutEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const padding = 8

      let top = 0
      let left = 0

      switch (position) {
        case 'top':
          top = triggerRect.top - tooltipRect.height - padding
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2)
          break
        case 'bottom':
          top = triggerRect.bottom + padding
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2)
          break
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2)
          left = triggerRect.left - tooltipRect.width - padding
          break
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2)
          left = triggerRect.right + padding
          break
      }

      // Keep tooltip within viewport
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (left < padding) left = padding
      if (left + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - tooltipRect.width - padding
      }
      if (top < padding) top = padding
      if (top + tooltipRect.height > viewportHeight - padding) {
        top = viewportHeight - tooltipRect.height - padding
      }

      setTooltipPosition({ top, left })
      // Mark as positioned AFTER setting position - this triggers the fade-in
      setIsPositioned(true)
    }
  }, [isVisible, position])

  // Tooltip content - only render when visible, start invisible until positioned
  // This prevents the flash at (0,0) that occurs before position calculation
  const boxShadow = glow === 'white'
    ? [
        '0 0 0 1px rgba(255,255,255,0.18)',
        '0 0 24px rgba(255,255,255,0.50)',
        '0 0 56px rgba(255,255,255,0.24)',
        '0 10px 26px rgba(0,0,0,0.46)',
        '0 18px 52px rgba(0,0,0,0.58)',
      ].join(', ')
    : [
        '0 0 0 1px rgba(255,255,255,0.10)',
        '0 0 14px rgba(255,255,255,0.22)',
        '0 0 28px rgba(255,255,255,0.10)',
        '0 8px 22px rgba(0,0,0,0.42)',
        '0 16px 44px rgba(0,0,0,0.54)',
      ].join(', ');
  const glowFilter = glow === 'white'
    ? 'drop-shadow(0 0 12px rgba(255,255,255,0.28))'
    : 'drop-shadow(0 0 8px rgba(255,255,255,0.12))';

  // Transform origin should point toward the trigger element so scale animation
  // expands outward from the hover point, not from default center
  const transformOriginMap: Record<string, string> = {
    top: 'bottom center',
    bottom: 'top center',
    left: 'center right',
    right: 'center left',
  }

  // Layer 1 render guard: visibleRef is set synchronously — if dismiss was
  // called but React hasn't re-rendered yet, the ref prevents stale portals.
  if (isVisible && !visibleRef.current) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Tooltip] visibleRef guard prevented stale render (id=%d)', idRef.current)
    }
  }

  const tooltipContent = isVisible && visibleRef.current && content && (
    <div
      ref={tooltipRef}
      className={`
        fixed z-[10100] px-3 py-2 text-sm
        border border-slate-200/80 rounded-lg
        text-slate-200 max-w-xs whitespace-pre-wrap
        ease-out
        ${isPositioned ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
      `}
      style={{
        // Use -9999 as initial position until positioned, then use calculated position
        top: tooltipPosition?.top ?? -9999,
        left: tooltipPosition?.left ?? -9999,
        pointerEvents: interactive ? 'auto' : 'none',
        backgroundColor: 'rgba(15, 23, 42, 0.82)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow,
        filter: glowFilter,
        // Only transition opacity and transform - NOT top/left which would cause
        // the tooltip to visibly fly from -9999px to its position
        transition: 'opacity 150ms ease-out, transform 150ms ease-out',
        transformOrigin: transformOriginMap[position] || 'bottom center',
      }}
      onMouseEnter={interactive ? showTooltip : undefined}
      onMouseLeave={interactive ? scheduleHide : undefined}
    >
      {content}
    </div>
  )

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={scheduleHide}
        // Focus/blur are wired ONLY for gated (showOnlyIfHidden) tooltips so a
        // sighted keyboard user can discover a collapsed icon-only tab's name.
        // showTooltip applies the same gate, so focus in a wide/label-visible
        // state is a no-op. Left off for ungated tooltips to avoid changing the
        // hover-only behaviour every other InstantTooltip caller relies on.
        onFocus={showOnlyIfHidden ? showTooltip : undefined}
        onBlur={showOnlyIfHidden ? scheduleHide : undefined}
        className={`inline ${className}`}
      >
        {children}
      </span>
      {isMounted && tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  )
}
